import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Flex, Pagination, ScrollArea, Tabs } from '@mantine/core';

import {
  useGetPatientQuery,
  useGetPatientTransmissionReportsPageQuery
} from '@/store/slices/api/patientsApiSlice';
import { useUnDismissReportMutation } from '@/store/slices/api/reportsApiSlice';
import withErrorBoundary from '@/utils/withErrorBoundary';
import { useTransmissionReportFeature } from '@/hooks/useTransmissionReportFeature';
import InfoMessage from '@/components/InfoMessage';
import SkeletonList from '@/components/SkeletonList';

import CreateReportTrigger from '@/pages/physician/TransmissionReportPage/CreateReport/ReportCreateTrigger';
import {
  GetPatientTxReportsRequest,
  MiniTxReport,
  ReportColor,
  ReportType
} from '@/types/transmissionReport';
import ConfirmationModal from '@/ui/ConfirmationModal';
import PatientReportCard from './PatientReportCard';
import styles from './PatientsReportList.module.scss';
import ViewAndDownloadModalWithBoundary from '../ViewAndDownloadModal';

const REPORT_PAGE_SIZE = 10;
const PIN_PAGER_AT = 8;

const PatientReportsList = () => {
  const { uuid: patientId } = useParams() as { uuid: string };
  const [activeTab, setActiveTab] = useState<string>('all');
  const [page, setPage] = useState(1);
  const { data: patient } = useGetPatientQuery(patientId);
  const [downloadReport, setDownloadReport] = useState<MiniTxReport | null>(null);
  const [activateReportId, setActivateReportId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activateReportMutation, { isLoading: isActivating }] = useUnDismissReportMutation();
  const navigate = useNavigate();
  const { getSpecificReportRoute } = useTransmissionReportFeature();

  const getFilterParams = (): GetPatientTxReportsRequest => {
    switch (activeTab) {
      case 'routine':
        return {
          colors: [ReportColor.Green, ReportColor.Gray]
        };
      case 'alerts':
        return {
          colors: [ReportColor.Red, ReportColor.Yellow]
        };
      case 'in-clinic':
        return {
          transmissionTypes: [ReportType.InClinic]
        };
      default:
        return {};
    }
  };

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch
  } = useGetPatientTransmissionReportsPageQuery(
    {
      patientId,
      page,
      limit: REPORT_PAGE_SIZE,
      ...getFilterParams()
    },
    {
      refetchOnMountOrArgChange: true
    }
  );

  const reports = data?.data ?? [];
  const totalPages = data?.totalPages ?? 1;

  const isCombinedPdf =
    patient?.clinic?.parent?.saveCombinedPDF ?? patient?.clinic?.saveCombinedPDF ?? false;

  const handleDownloadReport = useCallback((report: MiniTxReport) => {
    setDownloadReport(report);
  }, []);

  const handleActivateReportModal = useCallback((id: number | null) => {
    setActivateReportId(id);
    setIsModalOpen(!!id);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setActivateReportId(null);
  }, []);

  const confirmChanges = useCallback(async () => {
    if (activateReportId) {
      try {
        await activateReportMutation(activateReportId).unwrap();
        closeModal();
        navigate(getSpecificReportRoute(activateReportId.toString()));
      } catch (e) {
        closeModal();
      }
    }
  }, [activateReportId, activateReportMutation, closeModal, navigate]);

  if (error) {
    return (
      <Flex direction={'column'} gap={'md'}>
        <InfoMessage
          title={'Error Loading Reports'}
          message={'Please try refreshing the page or contact support if the issue persists.'}
          c="red"
        />
      </Flex>
    );
  }

  const pinPager = reports.length >= PIN_PAGER_AT;
  const pager =
    totalPages > 1 ? (
      <Flex justify="center" py="sm" style={{ flexShrink: 0 }}>
        <Pagination total={totalPages} value={page} onChange={setPage} size="sm" color="plum" />
      </Flex>
    ) : null;

  return (
    <Flex direction="column" flex={1} mih={0} h="100%">
      <Flex className={styles.filterHeaderSection} justify={'space-between'} align={'center'}>
        <Tabs
          classNames={{
            tab: styles.filterTabs
          }}
          value={activeTab}
          onChange={(value) => {
            setActiveTab(value || 'all');
            setPage(1);
          }}
          variant="pills">
          <Tabs.List>
            <Tabs.Tab value="all">All</Tabs.Tab>
            <Tabs.Tab value="routine">Routine</Tabs.Tab>
            <Tabs.Tab value="alerts">Alerts</Tabs.Tab>
            <Tabs.Tab value="in-clinic">In Clinic</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <CreateReportTrigger patientId={patientId} refetch={refetch} />
      </Flex>
      {patient && downloadReport && (
        <ViewAndDownloadModalWithBoundary
          report={downloadReport}
          isCombinedPdf={isCombinedPdf}
          handleDownloadReport={() => {
            setDownloadReport(null);
          }}
          patient={patient}
        />
      )}

      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" scrollbarSize={5} offsetScrollbars>
        <Flex direction={'column'} my={8}>
          {isLoading || isFetching ? (
            <SkeletonList items={5} itemHeight="40px" />
          ) : reports.length > 0 ? (
            reports.map((report) => (
              <PatientReportCard
                key={report.id}
                report={report}
                handleDownloadReport={handleDownloadReport}
                handleActiveReport={handleActivateReportModal}
              />
            ))
          ) : (
            <InfoMessage
              title={'No Reports'}
              message={
                activeTab === 'all'
                  ? 'No reports available for this patient.'
                  : `No ${activeTab} reports found for this patient.`
              }
            />
          )}
        </Flex>
        {!pinPager && pager}
      </ScrollArea>
      {pinPager && pager}
      <ConfirmationModal
        isOpen={isModalOpen}
        onConfirm={confirmChanges}
        onCancel={closeModal}
        onClose={closeModal}
        title="Confirm Activation"
        description="Activating will restore the report's details in Rhythm Synergy using the dismissed report's information."
        isLoading={isActivating}
        primaryButtonText={'Confirm'}
        secondaryButtonText={'Cancel'}
      />
    </Flex>
  );
};

const PatientReportsListWithBoundary = withErrorBoundary(PatientReportsList, {
  fallback: (
    <InfoMessage
      title={'Failed To Load Reports'}
      message={'Please refresh the page or contact support if the issue persists.'}
    />
  )
});

export default PatientReportsListWithBoundary;
